import { type JSX, Suspense, lazy, useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Badge,
  Box,
  Button,
  Group,
  Loader,
  Paper,
  SegmentedControl,
  SimpleGrid,
  Stack,
  Table,
  Text,
  Tooltip,
} from '@mantine/core';
import { type Home as Dados, api, explicarMotivo } from '../lib/api.js';
import {
  BlocoDeAjuda,
  FaixaDeNumeros,
  Numero,
  Secao,
  SemDadosSuficientes,
  Vazio,
  porcento,
} from './ui.js';

/**
 * O gráfico entra por `lazy`.
 *
 * `@mantine/charts` carrega o Recharts, que são ~125 KB comprimidos — mais da
 * metade do painel inteiro por causa de UM gráfico de barras. Separado em
 * pedaço próprio, ele só é baixado quando existe série para desenhar, e no
 * estado atual da base (poucos dias com dado) isso é NUNCA. Quem abre o painel
 * para atender não paga por um gráfico que ainda não aparece.
 */
const BarChart = lazy(() =>
  import('@mantine/charts').then((m) => ({ default: m.BarChart })),
);

/**
 * Tela inicial: "como estamos" antes de escolher uma seção.
 *
 * A ordem dos blocos é a ordem em que as perguntas importam, e ela não é a
 * ordem dos dados: AÇÃO primeiro (o que precisa de mim agora), depois
 * resultado, depois volume, depois padrão de horário, e por último o que está
 * indo mal. A aba Métricas continua existindo intacta como ferramenta de
 * CALIBRAÇÃO — histograma de cosseno, LOW/HIGH, precisão aparente.
 *
 * DADO INSUFICIENTE É O CASO NORMAL. A base tem poucas dezenas de
 * atendimentos. Cada gráfico aqui tem um estado próprio para isso, dizendo o
 * que falta e a partir de quando aparece — porque uma linha de três pontos
 * sugere tendência onde só há ruído.
 */

/** Períodos oferecidos. 30 dias é o padrão: é o mês em que a secretaria pensa. */
const PERIODOS = [
  { value: '7', label: '7 dias' },
  { value: '30', label: '30 dias' },
  { value: '90', label: '90 dias' },
];

const ROTULO_MOTIVO: Record<string, string> = {
  solicitado: 'o aluno pediu atendente',
  baixa_confianca: 'baixa confiança na resposta',
  humano_assumiu: 'um humano assumiu',
  nao_informado: 'não informado',
};

function espera(minutos: number): string {
  if (minutos < 60) return `${minutos}min úteis`;
  const horas = Math.floor(minutos / 60);
  const resto = minutos % 60;
  return resto === 0 ? `${horas}h úteis` : `${horas}h${String(resto).padStart(2, '0')} úteis`;
}

/**
 * Preenche os dias sem mensagem.
 *
 * O servidor devolve só os dias com dado (é o que o banco agrupa). Desenhar a
 * série sem os buracos comprimiria o tempo: três dias esparsos num mês
 * apareceriam como três dias seguidos.
 */
function serieCompleta(
  serie: readonly { dia: string; mensagens: number; transbordos: number }[],
  dias: number,
): { dia: string; rotulo: string; mensagens: number; transbordos: number }[] {
  const por = new Map(serie.map((d) => [d.dia, d]));
  const hoje = new Date();
  const saida: { dia: string; rotulo: string; mensagens: number; transbordos: number }[] = [];

  for (let i = dias - 1; i >= 0; i--) {
    const d = new Date(hoje.getFullYear(), hoje.getMonth(), hoje.getDate() - i);
    // Chave no fuso local, igual à que o servidor produziu com `AT TIME ZONE`.
    const chave = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(
      d.getDate(),
    ).padStart(2, '0')}`;
    const achado = por.get(chave);
    saida.push({
      dia: chave,
      rotulo: `${String(d.getDate()).padStart(2, '0')}/${String(d.getMonth() + 1).padStart(2, '0')}`,
      mensagens: achado?.mensagens ?? 0,
      transbordos: achado?.transbordos ?? 0,
    });
  }
  return saida;
}

export function Home({
  aoIrPara,
  aoVerConversa,
}: {
  /** Cada número leva à tela correspondente: ação vem primeiro. */
  aoIrPara: (secao: 'transbordos' | 'conversas' | 'lacunas' | 'faqs' | 'metricas') => void;
  aoVerConversa?: (atendimentoId: string) => void;
}): JSX.Element {
  const [dados, setDados] = useState<Dados | null>(null);
  const [dias, setDias] = useState('30');
  const [erro, setErro] = useState<string | null>(null);

  const carregar = useCallback(async () => {
    try {
      setDados(await api.get<Dados>(`/admin/home?dias=${dias}`));
      setErro(null);
    } catch (e) {
      setErro(explicarMotivo(e, 'falha ao carregar a tela inicial'));
    }
  }, [dias]);

  useEffect(() => {
    void carregar();
  }, [carregar]);

  if (erro) {
    return (
      <Stack gap="sm" align="flex-start">
        <Alert color="red" variant="light" role="alert">
          {erro}
        </Alert>
        <Button onClick={() => void carregar()}>Tentar de novo</Button>
      </Stack>
    );
  }

  if (!dados) {
    return (
      <Group gap="xs">
        <Loader size="sm" />
        <Text c="dimmed">Carregando…</Text>
      </Group>
    );
  }

  const { precisaDeMim: p, periodo, volume, horario, indoMal } = dados;
  const nadaPendente =
    p.transbordosPendentes === 0 &&
    p.conversasPausadas === 0 &&
    p.lacunasPendentes === 0 &&
    p.faqsPendentesDeReindexacao === 0;

  return (
    <Stack gap={0}>
      {/* O seletor de período é da TELA, não de cada bloco: um por bloco faria
          o leitor comparar números de janelas diferentes sem perceber. */}
      <Group justify="space-between" align="flex-end" mb="lg" wrap="wrap" gap="sm">
        <div>
          <Text fz={24} fw={600} lh={1.2}>
            Como estamos
          </Text>
          <Text size="sm" c="dimmed">
            Período de {dados.dias} dias · modo {dados.modo}
          </Text>
        </div>
        <SegmentedControl size="xs" value={dias} onChange={setDias} data={PERIODOS} />
      </Group>

      {/*
        Os subtítulos de cada bloco já dizem para que o bloco serve, e cada
        estado vazio já explica por que está vazio. Sobrou o que nenhum deles
        tem lugar para dizer: a ORDEM de leitura, o alcance do seletor de
        período e o que "modo sombra" significa — que é a informação mais
        consequente da tela e hoje é só uma palavra no cabeçalho.
      */}
      <Box mb="lg">
        <BlocoDeAjuda
          secoes={[
            {
              id: 'como-ler',
              titulo: 'Como ler esta tela',
              corpo: (
                <>
                  <Text size="sm">
                    <strong>Leia de cima para baixo e pare no 01 se houver número ali.</strong> O
                    bloco 01 é <strong>trabalho</strong>: tem gente esperando do outro lado. Do 02
                    em diante é <strong>leitura</strong> — conta como o período foi e não pede nada
                    de você hoje. Quem abre o painel com dez minutos olha o 01 e fecha.
                  </Text>
                  <Text size="sm">
                    <strong>O seletor de período no alto vale do bloco 02 em diante.</strong> O 01
                    é sempre <em>agora</em>: a fila deste momento, o bot calado neste momento, as
                    perguntas ainda sem resposta na base. Trocar de 7 para 90 dias não mexe nos
                    números do 01.
                  </Text>
                  <Text size="sm">
                    <strong>A palavra ao lado do período é o modo do bot, e ela muda tudo.</strong>{' '}
                    Em <strong>sombra</strong>, nada do que o bot escreve chega ao aluno: a
                    resposta vai como relatório para o grupo da secretaria no WhatsApp, para
                    alguém conferir se estava certa — e é dessa conferência que sai a calibração,
                    em Métricas. Em <strong>live</strong>, o aluno recebe. A tela é a mesma nos
                    dois casos; essa palavra é a única diferença visível, e vale olhar antes de
                    concluir qualquer coisa dos números.
                  </Text>
                  <Text size="sm" c="dimmed">
                    Bloco vazio não é defeito. Cada um explica no próprio lugar por que está vazio
                    e o que faz o número aparecer — no começo, com a base nova, quase todos ficam
                    assim por semanas.
                  </Text>
                </>
              ),
            },
          ]}
        />
      </Box>

      {/* ---------------------------------------------------------------- */}
      <Secao
        numero="01"
        titulo="O que precisa de mim agora"
        subtitulo="Filas com alguém esperando do outro lado. Cada número abre a tela correspondente."
      >
        {nadaPendente && !p.conversasDesconhecidas ? (
          <Vazio titulo="Nenhuma pendência agora.">
            Quando um transbordo entrar na fila, o bot ficar calado numa conversa ou uma pergunta
            não encontrar resposta, o número aparece aqui — e esta tela é o lugar de olhar primeiro.
          </Vazio>
        ) : (
          <FaixaDeNumeros>
            <Numero
              rotulo="Transbordos pendentes"
              valor={p.transbordosPendentes}
              cor={p.transbordosPendentes > 0 ? 'red' : undefined}
              nota={p.transbordosPendentes > 0 ? 'alguém está esperando resposta' : null}
              aoClicar={() => aoIrPara('transbordos')}
            />
            <Numero
              rotulo="Conversas com o bot calado"
              valor={p.conversasDesconhecidas ? '—' : p.conversasPausadas}
              nota={
                p.conversasDesconhecidas
                  ? 'desconhecido: o Redis não respondeu'
                  : p.conversasPausadas > 0
                    ? 'o bot não responde nelas'
                    : null
              }
              aoClicar={() => aoIrPara('conversas')}
            />
            <Numero
              rotulo="Lacunas pendentes"
              valor={p.lacunasPendentes}
              nota={p.lacunasPendentes > 0 ? 'perguntas sem resposta na base' : null}
              aoClicar={() => aoIrPara('lacunas')}
            />
            <Numero
              rotulo="FAQs a reindexar"
              valor={p.faqsPendentesDeReindexacao}
              nota={p.faqsPendentesDeReindexacao > 0 ? 'editadas e ainda não buscáveis' : null}
              aoClicar={() => aoIrPara('faqs')}
            />
          </FaixaDeNumeros>
        )}

        {p.maisAntigo && (
          <Paper
            withBorder
            mt="sm"
            p="sm"
            radius="md"
            bg={p.maisAntigo.atrasado ? 'var(--mantine-color-red-light)' : undefined}
          >
            <Group justify="space-between" wrap="wrap" gap="xs">
              <Group gap="sm" wrap="wrap">
                <Text size="sm" fw={600}>
                  Esperando há mais tempo
                </Text>
                <Text size="sm" ff="monospace">
                  {p.maisAntigo.protocolo}
                </Text>
                <Text size="sm">{p.maisAntigo.nome ?? '(sem nome)'}</Text>
                {/* Etiqueta com TEXTO: o fundo vermelho é reforço, não sinal. */}
                <Badge
                  color={p.maisAntigo.atrasado ? 'red' : 'gray'}
                  variant={p.maisAntigo.atrasado ? 'filled' : 'light'}
                >
                  {espera(p.maisAntigo.minutosUteis)}
                </Badge>
                {p.maisAntigo.atrasado && (
                  <Text size="xs" c="red">
                    passou de 4h de atendimento
                  </Text>
                )}
              </Group>
              <Group gap="xs">
                {aoVerConversa && (
                  <Button
                    size="xs"
                    variant="light"
                    onClick={() => aoVerConversa(p.maisAntigo!.atendimentoId)}
                  >
                    Ver conversa
                  </Button>
                )}
                <Button size="xs" onClick={() => aoIrPara('transbordos')}>
                  Abrir a fila
                </Button>
              </Group>
            </Group>
          </Paper>
        )}
      </Secao>

      {/* ---------------------------------------------------------------- */}
      <Secao
        numero="02"
        titulo="Como foi o período"
        subtitulo="O que o bot resolveu sozinho e o que precisou de gente. A base da taxa é o atendimento, não a mensagem."
      >
        {periodo.atendimentos === 0 ? (
          <Vazio titulo={`Nenhum atendimento nos últimos ${dados.dias} dias.`}>
            Os números aparecem assim que o bot responder a primeira mensagem do período. Se o bot
            está em modo sombra, ele registra tudo sem enviar nada ao aluno — e estes números
            contam as simulações.
          </Vazio>
        ) : (
          <FaixaDeNumeros>
            <Numero rotulo="Mensagens respondidas" valor={periodo.mensagens} />
            <Numero
              rotulo="Resolvidas com FAQ"
              valor={periodo.respondidas}
              nota={`de ${periodo.mensagens} mensagens`}
            />
            <Numero
              rotulo="Transbordos"
              valor={periodo.transbordos}
              nota={periodo.taxaTransbordo !== null ? porcento(periodo.taxaTransbordo) : null}
            />
            <Numero
              rotulo="Taxa de resolução"
              valor={porcento(periodo.taxaResolucao)}
              nota={`${periodo.atendimentos} atendimento(s)`}
            />
          </FaixaDeNumeros>
        )}
      </Secao>

      {/* ---------------------------------------------------------------- */}
      <Secao
        numero="03"
        titulo="Volume"
        subtitulo="Mensagens por dia, com os transbordos ao lado. Mostra tendência e se o bot está melhorando."
      >
        {!volume.suficienteParaTendencia ? (
          <SemDadosSuficientes
            titulo={
              volume.diasComDados === 0
                ? 'Nenhum dia com mensagem no período.'
                : `Ainda não há dias suficientes para mostrar tendência (${volume.diasComDados} de ${volume.diasParaTendencia}).`
            }
          >
            O gráfico aparece a partir de {volume.diasParaTendencia} dias com mensagem. Até lá, uma
            linha de poucos pontos sugeriria uma tendência que o dado não sustenta — os números do
            bloco 02 continuam valendo.
          </SemDadosSuficientes>
        ) : (
          <Suspense
            fallback={
              <Group gap="xs" h={240} align="center">
                <Loader size="sm" />
                <Text c="dimmed" size="sm">
                  Carregando o gráfico…
                </Text>
              </Group>
            }
          >
            <BarChart
              h={240}
              data={serieCompleta(volume.serie, dados.dias)}
              dataKey="rotulo"
              withLegend
              tooltipAnimationDuration={0}
              series={[
                { name: 'mensagens', label: 'mensagens', color: 'azul.6' },
                // Série separada, não empilhada: transbordo acontece DENTRO de
                // uma rodada já contada em "mensagens", e empilhar somaria o
                // mesmo evento duas vezes.
                { name: 'transbordos', label: 'transbordos', color: 'orange.6' },
              ]}
            />
          </Suspense>
        )}
      </Secao>

      {/* ---------------------------------------------------------------- */}
      <Secao
        numero="04"
        titulo="Quando chegam as mensagens"
        subtitulo="Informação de negócio além do bot: se boa parte chega fora do horário, o horário de atendimento é que está no lugar errado."
      >
        {horario.total === 0 ? (
          <Vazio titulo="Nenhuma mensagem no período.">
            O mapa de horário se preenche com qualquer volume — ele existe justamente para
            funcionar com amostra pequena, diferente de uma linha de tendência.
          </Vazio>
        ) : (
          <Stack gap="xs">
            <MapaDeHoras horas={horario.horas} />
            <Group gap="sm" wrap="wrap">
              <Text size="sm">
                <strong>{horario.foraDoHorarioComercial}</strong> de {horario.total} mensagens
                chegaram das 18h em diante
                {horario.fracaoForaDoHorario !== null
                  ? ` (${porcento(horario.fracaoForaDoHorario)})`
                  : ''}
                .
              </Text>
              {!horario.amostraSuficiente && (
                <Text size="xs" c="dimmed">
                  Amostra pequena ({horario.total} mensagens): o padrão fica confiável a partir de{' '}
                  {horario.amostraMinima}.
                </Text>
              )}
            </Group>
          </Stack>
        )}
      </Secao>

      {/* ---------------------------------------------------------------- */}
      <Secao
        numero="05"
        titulo="O que está indo mal"
        subtitulo="Onde o bot errou ou desistiu. É a lista de trabalho da curadoria."
        acao={
          <Button size="xs" variant="subtle" onClick={() => aoIrPara('metricas')}>
            Ver métricas de calibração
          </Button>
        }
      >
        <SimpleGrid cols={{ base: 1, lg: 2 }} spacing="md">
          <Stack gap="xs">
            <Text size="sm" fw={600}>
              FAQs com veredito negativo
            </Text>
            {indoMal.faqsComVereditoNegativo.length === 0 ? (
              <Vazio titulo="Nenhuma FAQ marcada com 👎 no período.">
                O veredito vem da reação da secretaria no relatório de sombra. Sem 👎 registrado,
                não há FAQ acusada — reagir aos relatórios é o que alimenta esta lista.
              </Vazio>
            ) : (
              <Table.ScrollContainer minWidth={380}>
                <Table>
                  <Table.Thead>
                    <Table.Tr>
                      <Table.Th>FAQ</Table.Th>
                      <Table.Th w={70}>👎</Table.Th>
                      <Table.Th w={70}>usos</Table.Th>
                    </Table.Tr>
                  </Table.Thead>
                  <Table.Tbody>
                    {indoMal.faqsComVereditoNegativo.map((f) => (
                      <Table.Tr key={f.faqId}>
                        <Table.Td>
                          <Text size="sm">{f.pergunta ?? '(FAQ removida da base)'}</Text>
                          {f.categoria && (
                            <Text size="xs" c="dimmed">
                              {f.categoria}
                            </Text>
                          )}
                        </Table.Td>
                        <Table.Td>
                          {/* Número + cor: a cor sozinha não diria "ruim". */}
                          <Text size="sm" c="red" fw={600}>
                            {f.negativos}
                          </Text>
                        </Table.Td>
                        <Table.Td>
                          <Text size="sm" ff="monospace">
                            {f.acionamentos}
                          </Text>
                        </Table.Td>
                      </Table.Tr>
                    ))}
                  </Table.Tbody>
                </Table>
              </Table.ScrollContainer>
            )}
          </Stack>

          <Stack gap="xs">
            <Text size="sm" fw={600}>
              Onde o bot desiste
            </Text>
            {indoMal.transbordoPorNo.length === 0 ? (
              <Vazio titulo="Nenhum transbordo no período.">
                Quando houver, esta tabela mostra o nó do menu em que o bot desistiu e a taxa sobre
                quem passou por ele — taxa alta em nó pouco usado é um problema diferente de
                volume alto em nó movimentado.
              </Vazio>
            ) : (
              <Table.ScrollContainer minWidth={380}>
                <Table>
                  <Table.Thead>
                    <Table.Tr>
                      <Table.Th>Nó</Table.Th>
                      <Table.Th w={90}>transbordos</Table.Th>
                      <Table.Th w={80}>taxa</Table.Th>
                    </Table.Tr>
                  </Table.Thead>
                  <Table.Tbody>
                    {indoMal.transbordoPorNo.slice(0, 8).map((t) => (
                      <Table.Tr key={t.no}>
                        <Table.Td>
                          <Text size="sm" ff="monospace">
                            {t.no}
                          </Text>
                        </Table.Td>
                        <Table.Td>
                          <Text size="sm" ff="monospace">
                            {t.transbordos}/{t.atendimentosNoNo}
                          </Text>
                        </Table.Td>
                        <Table.Td>
                          <Text size="sm" ff="monospace">
                            {porcento(t.taxa)}
                          </Text>
                        </Table.Td>
                      </Table.Tr>
                    ))}
                  </Table.Tbody>
                </Table>
              </Table.ScrollContainer>
            )}

            {indoMal.transbordoPorMotivo.length > 0 && (
              <Stack gap={4} mt="xs">
                <Text size="sm" fw={600}>
                  Transbordo por motivo
                </Text>
                {indoMal.transbordoPorMotivo.map((m) => (
                  <Group key={m.motivo} justify="space-between" gap="sm">
                    <Text size="sm">{ROTULO_MOTIVO[m.motivo] ?? m.motivo}</Text>
                    <Text size="sm" ff="monospace" c="dimmed">
                      {m.total} · {porcento(m.fracao)}
                    </Text>
                  </Group>
                ))}
              </Stack>
            )}
          </Stack>
        </SimpleGrid>
      </Secao>
    </Stack>
  );
}

// ---------------------------------------------------------------------------

/**
 * Mapa de calor das 24 horas, em blocos de intensidade.
 *
 * Blocos, e não linha: com dezenas de mensagens uma linha por hora seria
 * ruído puro, enquanto a grade continua legível com amostra pequena — é
 * possível ler "chegou algo às 21h" com uma única mensagem.
 *
 * A intensidade é relativa ao PICO do próprio período, não a um máximo
 * absoluto: o que a grade mostra é a forma do dia, não o volume.
 */
function MapaDeHoras({ horas }: { horas: readonly { hora: number; mensagens: number }[] }): JSX.Element {
  const pico = Math.max(...horas.map((h) => h.mensagens), 1);

  return (
    <Box>
      <Group gap={3} wrap="nowrap" style={{ overflowX: 'auto' }}>
        {horas.map((h) => {
          const intensidade = h.mensagens / pico;
          return (
            <Tooltip
              key={h.hora}
              label={`${String(h.hora).padStart(2, '0')}h — ${h.mensagens} mensagem(ns)`}
              withArrow
            >
              <Stack gap={2} align="center" style={{ flex: '1 0 26px', minWidth: 26 }}>
                <Box
                  h={34}
                  w="100%"
                  style={{
                    borderRadius: 4,
                    border: '1px solid var(--mantine-color-default-border)',
                    // Casa vazia fica com o fundo da superfície: sem cor
                    // nenhuma, para o contraste do preenchido ser o sinal.
                    // `light-dark`: no tema escuro o tom 6 some contra o
                    // fundo, e a casa de intensidade baixa ficaria invisível.
                    // A contagem impressa abaixo é o sinal que não depende de
                    // cor nenhuma.
                    backgroundColor:
                      h.mensagens === 0
                        ? 'transparent'
                        : `color-mix(in srgb, light-dark(var(--mantine-color-azul-6), var(--mantine-color-azul-4)) ${Math.round(
                            25 + intensidade * 75,
                          )}%, transparent)`,
                  }}
                />
                {/* O número vai DENTRO da grade quando existe: quem não
                    distingue intensidade de cor lê a contagem. */}
                <Text size="xs" c={h.mensagens > 0 ? undefined : 'dimmed'} ff="monospace" lh={1}>
                  {h.mensagens > 0 ? h.mensagens : '·'}
                </Text>
                <Text size="xs" c="dimmed" ff="monospace" lh={1}>
                  {String(h.hora).padStart(2, '0')}
                </Text>
              </Stack>
            </Tooltip>
          );
        })}
      </Group>
      <Text size="xs" c="dimmed" mt={6}>
        Intensidade relativa ao pico do período ({pico} mensagem(ns) na hora mais movimentada). O
        número em cada casa é a contagem.
      </Text>
    </Box>
  );
}
